<template>
  <div class="Wordcloud">
    <div v-if="showLoremText" class="border p-3 mb-4">
      <h1 class="font-medium text-gray-900 leading-tight">
        There is not enough information yet to display this section.
      </h1>
      <p class="mb-0 text-gray-700">
        You can help us by creating topics and assigning tags to them.
      </p>
      <lm-button mini class="mt-3" @click="goToTopics">Go to topics</lm-button>
    </div>
    <lm-logo v-if="showSpinner" />
    <div v-else @click="summary && goToCloud()">
      <cloud
        v-if="keywords.words && keywords.words.length > 0"
        ref="cloud"
        :data="keywords.words"
        :font-size-mapper="fontSizeMapper"
        :fill-mapper="fillMapper"
        :font="fontFamily"
        :width="width"
        :height="height"
        :on-word-click="onKeywordSelect"
        :on-word-over="onKeywordOver"
        :on-word-out="onKeywordOut"
        @cloudUpdated="onCloudUpdated"
      />
      <div
        v-if="
          !summary && keywords.meta && keywords.meta.n < keywords.meta.total
        "
        class="flex justify-center items-center"
      >
        <lm-button mini class="" @click="loadMore">
          Load more
        </lm-button>
      </div>
    </div>
  </div>
</template>
<script>
import {mapGetters} from 'vuex'
import LmButton from '@/components/ui/LmButton'
import Cloud from '@/components/viz/Cloud.vue'
import LmLogo from '@/components/ui/LmLogo'
import {palette} from '@/components/viz/Palette'
const KEYWORDS_N = 30

export default {
  name: 'WordCloud',
  components: {LmLogo, LmButton, Cloud},
  props: {
    selectedKeywords: {type: Array, default: () => []},
    cloudData: {type: Object, default: () => ({})},
    summary: {type: Boolean, default: false}
  },
  data() {
    const fontSizeMapper = this.summary
      ? word => (word.value / this.keywords.meta.max) * 20 + 12
      : word => (word.value / this.keywords.meta.max) * 35 + 15
    return {
      keywords: {},
      fontSizeMapper,
      highlightKeywords: [],
      fillMapper: word => {
        if (!this.keywords.meta) return
        const actor = this.getActorByID(word.actorId)
        const colorIndex = actor.color.split('color')[1]
        const value = Math.max(
          0.25,
          (0.75 * word.value) / (1.5 * this.keywords.meta.max)
        )
        let alpha = 1
        if (this.selectedKeywords.length > 0) {
          alpha = this.selectedKeywords.some(
            keyword => keyword.text === word.text
          )
            ? 2
            : 0
        }
        if (this.highlightKeywords.length > 0) {
          alpha = this.highlightKeywords.some(
            keyword => keyword.text === word.text
          )
            ? 2
            : alpha
        }
        return palette[colorIndex](value * alpha)
      },
      width: 0,
      height: 0,
      nKeywords: KEYWORDS_N,
      fontFamily: '"Space Mono", monospace',
      showSpinner: false,
      showLoremText: false,
      timeout: null
    }
  },
  computed: {
    ...mapGetters(['getActorByID', 'selectedActor', 'currentLandscape'])
  },
  watch: {
    selectedActor() {
      this.emitReload()
    },
    currentLandscape() {
      this.emitReload()
    },
    selectedKeywords() {
      if (this.$refs.cloud) this.$refs.cloud.updateColorFill()
    },
    highlightKeywords() {
      if (this.$refs.cloud) this.$refs.cloud.updateColorFill()
    },
    cloudData: {
      deep: true,
      handler(newData, oldData) {
        if (JSON.stringify(newData) !== JSON.stringify(oldData))
          this.buildCloud(newData)
      }
    }
  },
  created() {
    if (Object.keys(this.cloudData).length === 0) {
      this.emitReload()
    }
  },
  mounted() {
    window.addEventListener('resize', this.onResize)
    this.update()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    emitReload() {
      this.$emit('reload', this.nKeywords)
    },
    goToCloud() {
      if (this.keywords.words && this.keywords.words.length > 0) {
        const cloudPath = {name: 'wordcloud'}
        if (this.selectedActor)
          cloudPath.params = {actor: this.selectedActor.pathArr}
        this.$router.push(cloudPath)
      }
    },
    goToTopics() {
      const path = {name: 'topics'}
      if (this.selectedActor) {
        path.params = {actor: this.selectedActor.pathArr}
      }
      this.$router.push(path)
    },
    onCloudUpdated(n) {
      this.$emit('cloudUpdated', n)
    },
    onKeywordSelect(word) {
      if (!this.summary) this.$emit('keywordSelect', word)
    },
    onKeywordOver(word) {
      if (this.summary) return
      this.highlightKeywords.push(word)
    },
    onKeywordOut(word) {
      if (this.summary) return
      const wordIndex = this.highlightKeywords.findIndex(
        w => word.text === w.text
      )
      this.highlightKeywords.splice(wordIndex, 1)
    },
    onResize() {
      if (!this.showSpinner && !this.showLoremText) {
        this.showSpinner = true
        this.width = 0
        this.height = 0
      }
      clearTimeout(this.timeout)
      this.timeout = setTimeout(() => {
        this.showSpinner = false
        this.update()
      }, 400)
    },
    update() {
      // Refresh values
      this.width = this.$el.offsetWidth
      this.height = Math.max(250, this.$el.offsetHeight)
    },
    loadMore() {
      this.nKeywords += KEYWORDS_N
      this.update()
      this.$emit('reload', this.nKeywords)
    },
    buildCloud(keywords) {
      setTimeout(() => {
        this.showSpinner = false
        this.showLoremText = false
        this.keywords = keywords
        if (!keywords.words || keywords.words.length === 0) {
          this.showLoremText = true
          this.onCloudUpdated(0)
        }
      }, 100)
    }
  }
}
</script>

<style scoped></style>
